import React, { useState, useEffect } from 'react';
import Sidebar from '../components/SideBar';
import Header from '../components/Header';
import useGlobale from '../components/global';
import useMenuItems from '../components/Menu';
import {
  GroupeStats,
  GroupeFilters,
  GroupeList,
  GroupeDetail
} from '../components/groupe';
import api from '../components/api';
import Swal from 'sweetalert2';

const GroupePage = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { user } = useGlobale();
  const { menuItems } = useMenuItems();

  const [groupes, setGroupes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedGroupe, setSelectedGroupe] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [filters, setFilters] = useState({
    search: '',
    region: '',
    ordering: '-id'
  });

  useEffect(() => {
    const handleResize = () => {
      setIsSidebarOpen(window.innerWidth >= 1024);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchStats();
    fetchGroupes();
  }, [filters, currentPage]);

  const fetchStats = async () => {
    try {
      const response = await api.get('/api/utilisateur/admin/groupes/stats/');
      setStats(response.data);
    } catch (error) {
      console.error('Erreur lors de la récupération des stats:', error);
    }
  };

  const fetchGroupes = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          params.append(key, filters[key]);
        }
      });
      params.append('page', currentPage);
      params.append('page_size', 12);

      const response = await api.get(`/api/utilisateur/admin/groupes/?${params}`);
      setGroupes(response.data.results || []);
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      console.error('Erreur lors de la récupération des groupes:', error);
      setGroupes([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setCurrentPage(1);
  };

  const handleViewGroupe = (groupe) => {
    setSelectedGroupe(groupe);
    setShowDetail(true);
  };

  const handleUpdateGroupe = async (id, data) => {
    try {
      await api.put(`/api/utilisateur/admin/groupes/${id}/`, data);
      await Swal.fire({
        icon: 'success',
        title: 'Succès',
        text: 'Groupe mis à jour avec succès',
        confirmButtonColor: '#FF6B00',
      });
      fetchGroupes();
      fetchStats();
      setShowDetail(false);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Impossible de mettre à jour le groupe',
        confirmButtonColor: '#FF6B00',
      });
    }
  };

  const handleDeleteGroupe = async (id) => {
    const result = await Swal.fire({
      title: 'Confirmer la suppression',
      text: 'Êtes-vous sûr de vouloir supprimer ce groupe ? Cette action est irréversible et supprimera toutes les publications associées.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#FF6B00',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/api/utilisateur/admin/groupes/${id}/`);
        await Swal.fire({
          icon: 'success',
          title: 'Supprimé',
          text: 'Groupe supprimé avec succès',
          confirmButtonColor: '#FF6B00',
        });
        fetchGroupes();
        fetchStats();
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: 'Impossible de supprimer le groupe',
          confirmButtonColor: '#FF6B00',
        });
      }
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="flex min-h-screen bg-[#F4F8E9]">
      <Sidebar
        isOpen={isSidebarOpen}
        menuItems={menuItems}
        onClose={() => setIsSidebarOpen(false)}
      />

      <div className="flex-1 flex flex-col w-full">
        <Header
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
          user={user}
        />

        <main className="flex-1 w-full p-6">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900">
              Gestion des Groupes
            </h1>
            <p className="text-gray-600 mt-2">
              Gérez tous les groupes de la plateforme FONAP
            </p>
          </div>

          <div className="space-y-6">
            {/* Statistiques */}
            {stats && <GroupeStats stats={stats} />}

            {/* Filtres */}
            <GroupeFilters
              filters={filters}
              onFilterChange={handleFilterChange}
            />

            {/* Liste des groupes */}
            <GroupeList
              groupes={groupes}
              onView={handleViewGroupe}
              onDelete={handleDeleteGroupe}
              loading={loading}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>

          {/* Modal de détails */}
          {showDetail && selectedGroupe && (
            <GroupeDetail
              groupe={selectedGroupe}
              onClose={() => setShowDetail(false)}
              onUpdate={handleUpdateGroupe}
            />
          )}
        </main>
      </div>
    </div>
  );
};

export default GroupePage;